import { useQuery } from '@tanstack/react-query';

import type { GetCompleteWarrantyRegistrationResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get warranty registration status from a purchased vehicle
 */
export const getCompleteWarrantyRegistration = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetCompleteWarrantyRegistrationResponse> => {
	const response = await apiClient.get<GetCompleteWarrantyRegistrationResponse>(
		ordersEndpoints.completeWarrantyRegistration(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting warranty registration status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetCompleteWarrantyRegistration = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.completeWarrantyRegistration.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get warranty registration status without an order ID.');
			}
			return getCompleteWarrantyRegistration(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
